import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const OVERRIDE_KEY = 'b2b-theme-override';

const getSystemTheme = (): Theme =>
  window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';

const hasUserOverride = (): boolean => {
  const saved = localStorage.getItem(OVERRIDE_KEY);
  return saved === 'light' || saved === 'dark';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Dark theme temporarily disabled — to re-enable, uncomment the block below and remove the hardcoded 'light'
  const [theme, setThemeState] = useState<Theme>('light');
  /* const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem(OVERRIDE_KEY) as Theme | null;
    if (saved === 'light' || saved === 'dark') return saved;
    return getSystemTheme();
  }); */

  // Apply data-theme attribute on every theme change
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Follow system preference only when no user override is stored
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => {
      if (!hasUserOverride()) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleTheme = () =>
    setThemeState((t) => {
      const next = t === 'light' ? 'dark' : 'light';
      localStorage.setItem(OVERRIDE_KEY, next);
      return next;
    });

  const setTheme = (t: Theme) => {
    localStorage.setItem(OVERRIDE_KEY, t);
    setThemeState(t);
  };

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === 'dark', toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
